import React from 'react';
import { CreditCard, Banknote, Building2, DollarSign } from 'lucide-react';

const PaymentMethodSelector = ({
  paymentMethod,
  onPaymentMethodChange,
  paymentStatus,
  onPaymentStatusChange,
  amountPaid,
  onAmountPaidChange,
  totalAmount
}) => {
  const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: Banknote },
    { value: 'transfer', label: 'Bank Transfer', icon: Building2 },
    { value: 'pos', label: 'POS/ Card', icon: CreditCard },
    { value: 'credit', label: 'Credit/ Later', icon: DollarSign }
  ];

  const paymentStatuses = [
    { value: 'paid', label: 'Paid in Full', color: 'text-green-600' },
    { value: 'partial', label: 'Partial Payment', color: 'text-yellow-600' },
    { value: 'unpaid', label: 'Unpaid/ Credit', color: 'text-red-600' }
  ];

  const handleAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    onAmountPaidChange(amount);

    // Auto-update payment status based on amount
    if (amount === 0) {
      onPaymentStatusChange('unpaid');
    } else if (amount >= totalAmount) {
      onPaymentStatusChange('paid');
      onAmountPaidChange(totalAmount); // Cap at total
    } else {
      onPaymentStatusChange('partial');
    }
  };

  const amountDue = Math.max(0, totalAmount - amountPaid);

  return (
    <div className="space-y-4">
      {/* Payment Method Selection */}
      <div>
        <label className="block text-sm font-medium text-primary-black mb-2">
          Payment Method <span className="text-red-600">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {paymentMethods.map((method) => {
            const Icon = method.icon;
            const isSelected = paymentMethod === method.value;
            return (
              <button
                key={method.value}
                type="button"
                onClick={() => onPaymentMethodChange(method.value)}
                className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all ${
                  isSelected
                    ? 'border-accent-gold bg-yellow-50 text-accent-gold'
                    : 'border-secondary-lightGray text-secondary-gray hover:border-accent-gold hover:text-accent-gold'
                }`}
              >
                <Icon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">{method.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Payment Status Selection */}
      <div>
        <label className="block text-sm font-medium text-primary-black mb-2">
          Payment Status <span className="text-red-600">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {paymentStatuses.map((status) => {
            const isSelected = paymentStatus === status.value;
            return (
              <button
                key={status.value}
                type="button"
                onClick={() => onPaymentStatusChange(status.value)}
                className={`px-4 py-3 border-2 rounded-lg transition-all font-medium ${
                  isSelected
                    ? 'border-accent-gold bg-yellow-50 text-accent-gold'
                    : `border-secondary-lightGray ${status.color} hover:border-accent-gold`
                }`}
              >
                {status.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Payment Amount Details */}
      <div className="border border-secondary-lightGray rounded-lg p-4 bg-secondary-offWhite">
        <h4 className="font-semibold text-primary-black mb-3">Payment Details</h4>

        <div className="space-y-3">
          {/* Total Amount */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-secondary-gray">Total Amount:</span>
            <span className="text-lg font-bold text-primary-black">
              ₦{totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Amount Paid Input */}
          <div>
            <label className="block text-sm font-medium text-primary-black mb-1">
              Amount Paid
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray">₦</span>
              <input
                type="text"
                inputMode="decimal"
                value={amountPaid}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  handleAmountChange(value);
                }}
                className="w-full pl-8 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="0.00"
              />
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => handleAmountChange(totalAmount)}
                className="text-xs px-3 py-1 bg-accent-gold text-white rounded hover:bg-yellow-600 transition-colors"
              >
                Full Payment
              </button>
              <button
                type="button"
                onClick={() => handleAmountChange(totalAmount / 2)}
                className="text-xs px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                50%
              </button>
              <button
                type="button"
                onClick={() => handleAmountChange(0)}
                className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Amount Due */}
          <div className="flex justify-between items-center pt-3 border-t border-secondary-lightGray">
            <span className="text-sm font-medium text-secondary-gray">Amount Due:</span>
            <span className={`text-lg font-bold ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ₦{amountDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Payment Status Indicator */}
          <div className="flex items-center justify-center pt-2">
            {paymentStatus === 'paid' && (
              <div className="flex items-center gap-2 text-green-600">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Fully Paid</span>
              </div>
            )}
            {paymentStatus === 'partial' && (
              <div className="flex items-center gap-2 text-yellow-600">
                <div className="w-2 h-2 bg-yellow-600 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Partially Paid - Balance Due</span>
              </div>
            )}
            {paymentStatus === 'unpaid' && (
              <div className="flex items-center gap-2 text-red-600">
                <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Unpaid - Full Balance Due</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
